import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Search, User, Settings, Home, LayoutGrid, FolderOpen, CalendarCheck, ClipboardCheck, Users, FileText, History, GraduationCap, Smartphone, Trophy, BarChart3, ShieldCheck, Wallet, ListOrdered, Video, Building2, Gavel, Award } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Logo } from "./Logo";
import { InstallButton } from "./InstallButton";
import { useAuth } from "../lib/auth";
import { api, wsUrl, label } from "../lib/api";
import { PORTALS, ORDER, portalFromPath, defaultPortal, portalNav } from "../lib/portals";
import { IconBox } from "./kit";
import { toast } from "sonner";

const ICONS = [[/panel=timeline/, ListOrdered], [/panel=prep|checklist/, ClipboardCheck], [/panel=sheets/, Users], [/tactic/, LayoutGrid], [/appoint/, CalendarCheck], [/teams|players|staff|squad/, Users], [/report/, FileText],
  [/referee\/matches|history/, History], [/assess/, Award], [/course|training/, GraduationCap], [/device/, Smartphone], [/notif/, Bell], [/profile/, User], [/setting/, Settings],
  [/fixture|match|competition/, Trophy], [/standing|analytic/, BarChart3], [/disciplin|approval/, Gavel], [/referees|affiliat|organis/, ShieldCheck], [/financ/, Wallet], [/video/, Video], [/club|admin/, Building2]];
const iconFor = (to) => ICONS.find(([r]) => r.test(to))?.[1] || FileText;
const slug = (l) => l.toLowerCase().replace(/[^a-z]+/g, "-");

const SideLink = ({ l, to, icon, onNav, end }) => (
  <NavLink to={to} end={end} onClick={onNav} data-testid={`nav-${slug(l)}`}
    className={({ isActive }) => `group flex items-center gap-3 rounded-[14px] px-2 py-1.5 text-sm font-semibold transition-colors ${isActive && !to.includes("?") ? "bg-white text-[#171B20] shadow-sm" : "text-[#343A40] hover:bg-white/70"}`}>
    {({ isActive }) => <><IconBox icon={icon || iconFor(to)} size="sm" active={isActive && !to.includes("?")} />{l}</>}
  </NavLink>
);

const NavList = ({ portal, onNav }) => {
  const { me } = useAuth();
  const n = portalNav(portal, me);
  return (
    <nav className="space-y-4" data-testid="sidebar-nav">
      <div className="space-y-1"><SideLink l={n.dash[0]} to={n.dash[1]} icon={Home} onNav={onNav} end /><SideLink l={n.primary[0]} to={n.primary[1]} icon={LayoutGrid} onNav={onNav} /></div>
      {n.groups.map(([g, items]) => (
        <div key={g}><div className="tro-label mb-1 px-2">{g}</div><div className="space-y-0.5">{items.map(([l, to]) => <SideLink key={to} l={l} to={to} onNav={onNav} end />)}</div></div>
      ))}
    </nav>
  );
};

const Sidebar = ({ portal }) => {
  const Icon = PORTALS[portal].icon;
  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4">
      <Link to="/dashboard" aria-label="TRO dashboard" data-testid="sidebar-logo-link"><Logo testId="sidebar-logo" /></Link>
      <div className="flex items-center gap-2 rounded-[14px] tro-metal px-3 py-2.5">
        <Icon className="h-4 w-4 text-[#343A40]" /><span className="text-sm font-bold">TRO {PORTALS[portal].label} Portal</span>
      </div>
      <NavList portal={portal} />
    </div>
  );
};

function BottomNav({ portal, onOthers, othersOpen }) {
  const { me } = useAuth();
  const loc = useLocation();
  const n = portalNav(portal, me);
  const primaryBase = n.primary[1].split("?")[0];
  const onPrimary = loc.pathname.startsWith(primaryBase) || (portal === "referee" && /\/referee\/matches\/[^/]+\/(tactical|controls)/.test(loc.pathname));
  const onDash = loc.pathname === n.dash[1];
  const Item = ({ l, icon, active, to, onClick, testid }) => {
    const body = <><IconBox icon={icon} size="nav" active={active} /><span className={`max-w-full truncate whitespace-nowrap px-0.5 text-[11px] font-bold ${active ? "text-[#171B20]" : "text-[#6C757D]"}`}>{l}</span></>;
    const cls = "flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 rounded-[16px] py-1";
    return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{body}</button>;
  };
  return (
    <nav className="pb-safe fixed inset-x-0 bottom-0 z-50 border-t border-[#E7EAEE] bg-white/95 px-3 pt-1.5 backdrop-blur-md lg:hidden" data-testid="bottom-nav">
      <div className="mx-auto flex max-w-md gap-2">
        <Item l="Dashboard" icon={Home} to={n.dash[1]} active={onDash && !othersOpen} testid="bottom-nav-dashboard" />
        <Item l={n.primary[0]} icon={LayoutGrid} to={n.primary[1]} active={onPrimary && !othersOpen} testid="bottom-nav-primary" />
        <Item l="Others" icon={FolderOpen} onClick={onOthers} active={othersOpen || (!onDash && !onPrimary)} testid="bottom-nav-others" />
      </div>
    </nav>
  );
}

function OthersSheet({ portal, open, onClose }) {
  const { me, signOut } = useAuth();
  const nav = useNavigate();
  const n = portalNav(portal, me);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-[22px] px-4 pb-28 pt-3" data-testid="others-sheet">
        <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-[#C5CBD2]" />
        <SheetTitle className="text-left text-lg font-extrabold" data-testid="mobile-navigation-title">Others</SheetTitle>
        <SheetDescription className="text-left text-sm" data-testid="mobile-navigation-description">All {PORTALS[portal].label} pages</SheetDescription>
        <div className="mt-4 space-y-5">
          {n.groups.map(([g, items]) => (
            <section key={g} data-testid={`others-group-${slug(g)}`}>
              <div className="tro-label mb-2">{g}</div>
              <div className="grid grid-cols-4 gap-2">{items.map(([l, to]) => (
                <Link key={to} to={to} onClick={onClose} data-testid={`others-${slug(l)}`} className="flex flex-col items-center gap-1.5 text-center">
                  <IconBox icon={iconFor(to)} /><span className="text-[11px] font-bold leading-tight text-[#343A40]">{l}</span>
                </Link>
              ))}</div>
            </section>
          ))}
          <button data-testid="others-logout-btn" onClick={() => { onClose(); signOut(); window.location.replace("/login"); }} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[14px] border border-[#E7EAEE] text-sm font-bold"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function NotifBell() {
  const [n, setN] = useState(0);
  const nav = useNavigate();
  useEffect(() => {
    let sock;
    api.get("/notifications").then((r) => setN(r.data.unread)).catch(() => {});
    try {
      sock = new WebSocket(wsUrl());
      sock.onmessage = (e) => {
        const m = JSON.parse(e.data);
        if (m.type === "notification") {
          setN((x) => x + 1);
          toast(m.notification.title, { description: m.notification.body });
        }
      };
    } catch {}
    return () => sock?.close();
  }, []);
  return (
    <button data-testid="notifications-bell" onClick={() => nav("/notifications")} className="relative rounded-lg p-2 hover:bg-[#E6E9ED]">
      <Bell className="h-5 w-5 text-[#343A40]" />
      {n > 0 && <span data-testid="notifications-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#F02030] px-1 text-[10px] font-bold text-white">{n}</span>}
    </button>
  );
}

function SearchBox() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (q.length < 2) return setRes(null);
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative hidden md:block">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#6C757D]" />
      <input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clubs, players, competitions"
        className="h-9 w-72 rounded-lg border border-[#E6E9ED] bg-[#F7F8F9] pl-9 pr-3 text-sm outline-none focus:border-[#C5CBD1] focus:bg-white" />
      {res && (
        <div className="absolute left-0 top-11 z-50 w-80 rounded-lg border border-[#E6E9ED] bg-white p-2 text-sm shadow-lg" data-testid="search-results">
          {[...res.clubs.map((c) => `Club · ${c.name}`), ...res.players.map((p) => `Player · ${p.first_name} ${p.last_name}`), ...res.competitions.map((c) => `Competition · ${c.name}`)].map((s) => <div key={s} className="rounded px-2 py-1.5 hover:bg-[#F7F8F9]">{s}</div>)}
          {!res.clubs.length && !res.players.length && !res.competitions.length && <div className="px-2 py-1.5 text-[#6C757D]">No results</div>}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { me, signOut } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const portal = portalFromPath(loc.pathname) || localStorage.getItem("tro_portal") || defaultPortal(me.portals);
  const active = me.portals.includes(portal) ? portal : defaultPortal(me.portals);
  useEffect(() => {
    localStorage.setItem("tro_portal", active);
  }, [active]);
  const mem = me.memberships.find((m) => m.status === "active" && m.org_name && PORTALS[active] && m.role !== "fan");
  return (
    <div className="flex min-h-screen bg-white">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-[#E7EAEE] bg-[#F7F8FA] lg:block"><Sidebar portal={active} /></aside>
      <OthersSheet portal={active} open={open} onClose={() => setOpen(false)} />
      <BottomNav portal={active} othersOpen={open} onOthers={() => setOpen(true)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-[#E6E9ED] bg-white/90 px-3 backdrop-blur-md md:px-6" data-testid="app-header">
          <Link to="/dashboard" className="shrink-0 lg:hidden" data-testid="mobile-header-logo-link"><Logo size={28} withText={false} testId="mobile-header-logo" /></Link>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="portal-switcher" className="flex items-center gap-2 rounded-lg border border-[#E6E9ED] px-3 py-1.5 text-sm font-bold hover:bg-[#F7F8F9]">
              {PORTALS[active].label}<ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="tro-label">Switch portal</DropdownMenuLabel>
              {ORDER.filter((p) => me.portals.includes(p)).map((p) => (
                <DropdownMenuItem key={p} data-testid={`portal-option-${p}`} onClick={() => nav(PORTALS[p].nav[0][1])} className="font-semibold">{PORTALS[p].label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {mem && <span className="hidden truncate rounded-full bg-[#F7F8F9] px-3 py-1 text-xs font-semibold text-[#343A40] xl:inline" data-testid="current-org">{mem.org_name} · {label(mem.role)}</span>}
          <select data-testid="season-selector" className="hidden h-8 rounded-lg border border-[#E6E9ED] bg-white px-2 text-xs font-bold md:block" defaultValue="2026"><option>2026</option></select>
          <div className="ml-auto flex items-center gap-1.5">
            <InstallButton />
            <SearchBox />
            <NotifBell />
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="profile-menu" className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-[#E6E9ED]">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#17191C] text-xs font-bold text-white">{me.user.name.slice(0, 1)}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel><div className="text-sm font-bold">{me.user.name}</div><div className="text-xs font-normal text-[#6C757D]">{me.user.email}</div></DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")}><User className="mr-2 h-4 w-4" />Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")}><Settings className="mr-2 h-4 w-4" />Settings</DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={() => { signOut(); window.location.replace("/login"); }}><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1500px] flex-1 p-4 pb-28 sm:p-6 sm:pb-28 lg:p-8" key={loc.pathname}>{children}</main>
      </div>
    </div>
  );
}
